import { supabase } from './supabaseClient';

// Flight A/B experiment: variant assignment + event tracking (#212).
//
// Events ride the existing `marketing-analytics` edge function as synthetic
// page-view paths under /_e/flight/... — no new backend surface. They join
// with real page views on session_id (same sessionStorage id) and carry the
// page's UTM params, so results segment by variant × audience in the same
// `marketing_page_views` table. Upgrade path: a dedicated events table later.
//
// Assignment modes (VITE_FLIGHT_AB_MODE):
//   'system' (default) — variant follows prefers-color-scheme (the shipped UX)
//   'split'            — sticky 50/50 random assignment (the measured A/B)
// Both are sticky via localStorage and logged with their source, so the
// dashboard can always tell how a visitor ended up in a variant. Sources:
//   param        — ?v= override (QA/shared links; not persisted, not deduped)
//   choice       — the visitor's own Dawn/Night toggle switch
//   split/system — fresh assignment by the active mode
//   stored-*     — sticky repeat of an earlier assignment (provenance kept)
//   direct       — landed straight on /flight-a|b with no prior assignment
//
// Analysis note: assignment (the arm) is the assign event; depth/cta events
// carry the variant actually VIEWED, and toggle events record arm switches —
// join per session rather than aggregating raw per-path counts.

export type FlightVariant = 'a' | 'b';
export type AssignmentSource =
  | 'param'
  | 'choice'
  | 'split'
  | 'system'
  | 'direct'
  | `stored-${string}`;

const STORE_KEY = 'lk-flight-variant';
const SESSION_KEY = 'lykbl_marketing_session_id'; // same id MarketingAnalytics uses
const ASSIGN_LOGGED_KEY = 'lk-flight-assign-logged';
const KNOWN_SOURCES = ['param', 'choice', 'split', 'system'] as const;

export function resolveFlightVariant(): { variant: FlightVariant; source: AssignmentSource } {
  // 1. Explicit QA/link override — used, never stored, never deduped.
  const v = new URLSearchParams(window.location.search).get('v');
  if (v === 'a' || v === 'quiet-floors') return { variant: 'a', source: 'param' };
  if (v === 'b' || v === 'night-shift') return { variant: 'b', source: 'param' };

  // 2. Sticky prior assignment or manual Dawn/Night choice, provenance kept.
  const stored = readStored();
  if (stored) {
    return {
      variant: stored.v,
      source: stored.src === 'choice' ? 'choice' : `stored-${stored.src}`,
    };
  }

  // 3. Fresh assignment.
  if (import.meta.env.VITE_FLIGHT_AB_MODE === 'split') {
    const variant: FlightVariant = Math.random() < 0.5 ? 'a' : 'b';
    persistVariant(variant, 'split');
    return { variant, source: 'split' };
  }
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const variant: FlightVariant = prefersDark ? 'b' : 'a';
  persistVariant(variant, 'system');
  return { variant, source: 'system' };
}

export function persistVariant(variant: FlightVariant, src: AssignmentSource): void {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify({ v: variant, src }));
  } catch (_) {
    /* storage unavailable — assignment simply won't stick */
  }
}

function readStored(): { v: FlightVariant; src: string } | null {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' && parsed !== null &&
      'v' in parsed && ((parsed as { v: unknown }).v === 'a' || (parsed as { v: unknown }).v === 'b')
    ) {
      const src = (parsed as { src?: unknown }).src;
      return {
        v: (parsed as { v: FlightVariant }).v,
        src: typeof src === 'string' && (KNOWN_SOURCES as readonly string[]).includes(src) ? src : 'unknown',
      };
    }
  } catch (_) {
    /* fall through */
  }
  return null;
}

/** Fire an event via the existing analytics function. Values are clipped to the
 * edge function's schema limits so an oversized UTM can't invalidate the whole
 * event. Returns a promise capped at 300ms so callers can await it before a
 * full-document navigation without a perceptible delay. */
export function trackFlightEvent(eventPath: string): Promise<void> {
  const sessionId = getSessionId();
  if (!sessionId) return Promise.resolve();
  const params = new URLSearchParams(window.location.search);
  const invoke = supabase.functions
    .invoke('marketing-analytics', {
      body: {
        path: eventPath.slice(0, 512),
        referrer: document.referrer || null,
        session_id: sessionId,
        utm_source: clean(params.get('utm_source'), 120),
        utm_medium: clean(params.get('utm_medium'), 120),
        utm_campaign: clean(params.get('utm_campaign'), 160),
      },
    })
    .then(() => undefined)
    .catch(() => undefined);
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, 300));
  return Promise.race([invoke, timeout]);
}

/** Log the landing assignment once per browser session — exactly one assign
 * event per session, whatever its source (a `param` QA visit is that one event
 * and is identifiable by its source). Callers navigating right after MUST
 * await the returned (capped) promise, or the request is aborted by the
 * unload. */
export function trackAssignmentOnce(variant: FlightVariant, source: AssignmentSource): Promise<void> {
  try {
    if (window.sessionStorage.getItem(ASSIGN_LOGGED_KEY)) return Promise.resolve();
    window.sessionStorage.setItem(ASSIGN_LOGGED_KEY, '1');
  } catch (_) {
    /* still log without dedupe if sessionStorage is unavailable */
  }
  return trackFlightEvent(`/_e/flight/assign/${variant}/${source}`);
}

/** Scroll-depth milestones (25/50/75/100) + CTA click tracking for a flight page.
 * 100% means the camera landed on the CTA band — the flight's conversion beat.
 * Also records a `direct` assignment when the visitor landed here without one. */
export function installFlightTracking(container: HTMLElement, variant: FlightVariant): void {
  container.dataset.flightTracking = variant;
  void trackAssignmentOnce(variant, 'direct'); // no-op if this session already has one

  // A ?at= restore (toggle switch or shared link) starts the page mid-flight;
  // pre-seed those milestones so restored depth isn't replayed as engagement
  // with THIS variant.
  const fired = new Set<number>();
  const at = Number.parseFloat(new URLSearchParams(window.location.search).get('at') ?? '');
  const MILESTONES = [25, 50, 75, 100];
  // Half-point tolerance: fractional-DPR/zoomed viewports can max out slightly
  // short of the exact bottom, which would otherwise make 100 unreachable.
  const EPSILON = 0.5;
  if (Number.isFinite(at)) {
    for (const m of MILESTONES) if (at * 100 >= m - EPSILON) fired.add(m);
  }

  const onScroll = () => {
    const max = document.body.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    const pct = (window.scrollY / max) * 100;
    for (const m of MILESTONES) {
      if (pct >= m - EPSILON && !fired.has(m)) {
        fired.add(m);
        void trackFlightEvent(`/_e/flight/depth/${variant}/${m}`);
      }
    }
    if (fired.size === MILESTONES.length) window.removeEventListener('scroll', onScroll);
  };
  window.addEventListener('scroll', onScroll, { passive: true });

  // CTA clicks: primary/secondary buttons + topbar chip. These are plain <a>
  // full navigations, so for a plain left-click we briefly await the (capped)
  // track call before navigating. Modified clicks (new tab/window intents)
  // keep their native behavior and the event is fire-and-forget.
  container.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target.closest('a') : null;
    if (!target) return;
    let kind: string | null = null;
    if (target.classList.contains('sw-btn--primary')) kind = 'primary';
    else if (target.classList.contains('sw-btn--ghost')) kind = 'secondary';
    else if (target.classList.contains('sw-topcta')) kind = 'topbar';
    if (!kind) return;
    const eventPath = `/_e/flight/cta/${variant}/${kind}`;
    if (isModifiedClick(e)) {
      void trackFlightEvent(eventPath);
      return;
    }
    e.preventDefault();
    const href = target.href;
    void trackFlightEvent(eventPath).then(() => {
      window.location.assign(href);
    });
  });
}

export function isModifiedClick(e: MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0 || e.defaultPrevented;
}

function getSessionId(): string | null {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const created = window.crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch (_) {
    return null;
  }
}

function clean(value: string | null, max: number): string | null {
  const cleaned = value?.trim().slice(0, max);
  return cleaned ? cleaned : null;
}
